import type { FieldValue, Timestamp } from 'firebase/firestore';

type FirestoreTimestampLike = {
    seconds: number;
    nanoseconds: number;
};

type FirestoreSentinelLike = {
    _methodName: string;
};

export type FirestoreDeleteFieldSentinel = {
    _methodName: 'deleteField';
};

type FirestoreSanitizedValue =
    | string
    | number
    | boolean
    | null
    | Date
    | FieldValue
    | Timestamp
    | FirestoreTimestampLike
    | FirestoreSentinelLike
    | FirestoreSanitizedValue[]
    | { [key: string]: FirestoreSanitizedValue };

export type FirestoreSanitizedDocument = Record<string, FirestoreSanitizedValue>;

const isRecord = (value: unknown): value is Record<string, unknown> => (
    value !== null && typeof value === 'object' && !Array.isArray(value)
);

const isTimestampLike = (value: Record<string, unknown>): value is FirestoreTimestampLike => (
    typeof value.seconds === 'number' && typeof value.nanoseconds === 'number'
);

const isFirestoreSentinelLike = (value: Record<string, unknown>): value is FirestoreSentinelLike => (
    typeof value._methodName === 'string'
);

export function createFirestoreDeleteFieldSentinel(): FirestoreDeleteFieldSentinel {
    return { _methodName: 'deleteField' };
}

export function isFirestoreDeleteFieldSentinel(value: unknown): value is FirestoreDeleteFieldSentinel {
    return isRecord(value) && value._methodName === 'deleteField';
}

/**
 * Sanitizes data for Firestore:
 * 1. Recursive cleaning.
 * 2. Removes `undefined` values from arrays/objects.
 * 3. Removes keys ending in 'Actions' (store functions).
 * 4. Removes functions.
 *
 * @param data - Any runtime data structure from app state or feature APIs.
 * @returns Sanitized data safe for Firestore, or `undefined` when a value should be omitted.
 */
export function sanitizeForFirestore(data: unknown): FirestoreSanitizedValue | undefined {
    // 1. Handle Undefined (Signal to remove)
    if (data === undefined) return;
    
    // 2. Handle Null (Pass through)
    if (data === null) return null;

    // 3. Handle Functions (Signal to remove)
    if (typeof data === 'function') return;

    // 4. Handle Primitives (Pass through)
    if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
        return data;
    }

    if (typeof data !== 'object') return;

    // 5. Handle Arrays
    if (Array.isArray(data)) {
        return data
            .map(item => sanitizeForFirestore(item))
            // Filter out undefined items only — null is a valid Firestore data type
            .filter((item): item is FirestoreSanitizedValue => item !== undefined);
    }

    // 6. Handle Firestore Types (Timestamp, FieldValue sentinels, etc)
    if (data instanceof Date) {
        return data;
    }

    if (!isRecord(data)) {
        return;
    }

    if (isTimestampLike(data) || isFirestoreSentinelLike(data)) {
        return data;
    }

    // 7. Handle Objects
    const result: Record<string, FirestoreSanitizedValue> = {};
    for (const [key, value] of Object.entries(data)) {
        // Strip Action groups explicitly
        if (key.endsWith('Actions')) continue;

        const sanitized = sanitizeForFirestore(value);

        // Only add key if value is not undefined.
        // (Nulls are preserved, Undefined/Functions are removed)
        if (sanitized !== undefined) {
            result[key] = sanitized;
        }
    }
    return result;
}

export function sanitizeDocumentForFirestore(data: unknown): FirestoreSanitizedDocument {
    const sanitized = sanitizeForFirestore(data);
    return isRecord(sanitized) ? sanitized : {};
}

export function omitInlineDataUrlFields<T extends object, K extends keyof T>(
    data: T,
    fields: readonly K[]
): T {
    const result = { ...data };
    for (const field of fields) {
        if (typeof result[field] === 'string' && result[field].trimStart().startsWith('data:')) {
            delete (result as Partial<T>)[field];
        }
    }
    return result;
}














